// AuthContext - provides authentication state across the app
import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { setExternalUserId, removeExternalUserId } from '@/services/onesignal';
import { isDespiaNative, safeDespia } from '@/utils/despia';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  isEmailVerified: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Key to track explicit logout - prevents session rehydration after logout
const LOGOUT_FLAG_KEY = 'evolve_explicit_logout';

// Helper to sync user ID with OneSignal via Despia native SDK
const syncDespiaOneSignalPlayerId = (userId: string) => {
  if (isDespiaNative()) {
    console.log('[Despia] Setting OneSignal player ID for user:', userId);
    safeDespia(`setonesignalplayerid://?user_id=${userId}`);
  }
};

// Helper to clear all local auth-related storage
const clearLocalAuthState = () => {
  console.log('[Auth] Clearing all local auth state');
  
  // Clear any session-related storage keys
  const keysToRemove: string[] = [];
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i);
    if (key && (key.includes('tier') || key.includes('session') || key.includes('auth'))) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach(key => sessionStorage.removeItem(key));
  
  // Clear Supabase local storage tokens (they persist session)
  const localKeysToCheck = ['sb-uduuoxmkpadoeeiegdxo-auth-token'];
  localKeysToCheck.forEach(key => {
    if (localStorage.getItem(key)) {
      localStorage.removeItem(key);
    }
  });
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  const isEmailVerified = user?.email_confirmed_at != null;

  useEffect(() => {
    // Check if user explicitly logged out - skip session rehydration
    const wasExplicitLogout = sessionStorage.getItem(LOGOUT_FLAG_KEY) === 'true';
    if (wasExplicitLogout) {
      console.log('[Auth] Explicit logout detected, skipping session rehydration');
      sessionStorage.removeItem(LOGOUT_FLAG_KEY);
      setUser(null);
      setSession(null);
      setLoading(false);
      return;
    }

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('[Auth] Auth state change:', event);
      
      // If we're in logout mode, ignore any session updates
      if (sessionStorage.getItem(LOGOUT_FLAG_KEY) === 'true') {
        console.log('[Auth] Ignoring auth state change during logout');
        return;
      }
      
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Link/unlink OneSignal user ID on auth state change (use email as External ID)
      if (session?.user?.email) {
        setExternalUserId(session.user.email);
      } else if (event === 'SIGNED_OUT') {
        removeExternalUserId();
      }

      // Sync Despia OneSignal player ID on every auth state change (uses user.id)
      if (session?.user?.id) {
        syncDespiaOneSignalPlayerId(session.user.id);
      }
    });

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      // Double-check logout flag in case it was set after initial check
      if (sessionStorage.getItem(LOGOUT_FLAG_KEY) === 'true') {
        console.log('[Auth] Logout flag detected during session fetch, clearing');
        setUser(null);
        setSession(null);
        setLoading(false);
        return;
      }
      
      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      // Link OneSignal user ID if already logged in (use email as External ID)
      if (session?.user?.email) {
        setExternalUserId(session.user.email);
      }

      // Sync Despia OneSignal player ID on app load (uses user.id)
      if (session?.user?.id) {
        syncDespiaOneSignalPlayerId(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const signOut = async () => {
    console.log('[Auth] Starting sign out process');
    
    // Step 1: Set logout flag FIRST to prevent any rehydration
    sessionStorage.setItem(LOGOUT_FLAG_KEY, 'true');
    
    // Step 2: Synchronously clear React state IMMEDIATELY
    setUser(null);
    setSession(null);
    
    // Step 3: Clear local storage/session storage auth data
    clearLocalAuthState();
    
    // Step 4: Remove OneSignal external user ID
    removeExternalUserId();
    
    // Step 5: Call Supabase signOut (this is async but state is already cleared)
    try {
      await supabase.auth.signOut({ scope: 'local' });
      console.log('[Auth] Supabase signOut completed');
    } catch (error) {
      console.error('[Auth] Supabase signOut error:', error);
      // Continue with navigation even if signOut fails
    }
    
    // Step 6: Force hard navigation to auth page
    // Use window.location for a full page reset, bypassing React Router
    console.log('[Auth] Forcing navigation to /auth');
    window.location.href = '/auth';
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, isEmailVerified, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};
